import React from "react";
import { Link } from "react-router-dom";

import DocsSnippet from "ui/DocsSnippet";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import { CHECK_OPTIONS } from "features/Checks/CheckConfig";

import { CheckConfigListQueryResult } from "./gql/Query.generated";
import { useRoutes } from "utils/routes";
import Grid, { GridColumn } from "ui/Grid";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import { useCheckConfigListQuery } from "./gql/Query.generated";

type Props = {
  organizationSlug: string;
  checkGroupAndName: string;
};

type CheckConfigType =
  CheckConfigListQueryResult["data"]["getCheckConfigs"][number] &
    Record<string, string>;

const CheckConfigList: React.FunctionComponent<Props> = ({
  organizationSlug,
  checkGroupAndName,
}) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const { serverCheckConfigure } = useRoutes();
  const { data, loading, error } = useCheckConfigListQuery({
    variables: {
      organizationSlug,
      checkGroupAndName: checkGroupAndName,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const [groupName, checkName] = checkGroupAndName.split("/", 2);
  const options =
    (CHECK_OPTIONS[groupName] && CHECK_OPTIONS[groupName][checkName]) || [];

  const checkConfigs: CheckConfigType[] = data.getCheckConfigs
    .map((c) => ({
      serverName: c.server.name,
      ...c.settings,
      ...c,
    }))
    .filter((c) => {
      return (
        searchTerm === "" || c.serverName.toLowerCase().includes(searchTerm)
      );
    });

  const baseColumns: GridColumn<
    (typeof checkConfigs)[number],
    keyof (typeof checkConfigs)[number]
  >[] = [
    {
      field: "serverName",
      header: "Server",
      width: "1fr",
      renderer: function ServerCell({ fieldData, rowData }) {
        return (
          <Link
            to={serverCheckConfigure(rowData.server.humanId, checkGroupAndName)}
          >
            {fieldData}
          </Link>
        );
      },
    },
    {
      field: "enabled",
      header: "Check Enabled?",
      width: "150px",
      renderer: function CheckEnabledCell({ fieldData }) {
        return fieldData ? "Yes" : "No";
      },
    },
  ];

  const columns = options.reduce<typeof baseColumns>((currCols, currOpt) => {
    return currCols.concat({
      field: currOpt.key,
      header: (
        <>
          {currOpt.title}{" "}
          <DocsSnippet title={currOpt.title} content={currOpt.help} />
        </>
      ),
      width: "250px",
    });
  }, baseColumns);

  return (
    <Panel title="Select Server To Configure" secondaryTitle={<SearchInput />}>
      <Grid
        data={checkConfigs}
        defaultSortBy="serverName"
        columns={columns}
        striped
      />
    </Panel>
  );
};

export default CheckConfigList;
